import { useCreateOrg } from '../../hooks/admin/useAdminMutations'
import { useAdminOrgs } from '../../hooks/admin/useAdminQueries'
import { OrgDto, CreateOrgDto } from '../../types/admin-types'
import { useQueryClient } from '@tanstack/react-query'
import React, { useState, useEffect, useMemo, useCallback } from 'react'

interface OrgAutocompleteProps {
    value: string | undefined // Selected org ID (for initial value)
    onChange: (org: OrgDto | null) => void // Simplified: Only passes OrgDto or null
    disabled?: boolean
    className?: string
    placeholder?: string // Allow custom placeholder
}

const OrgAutocomplete: React.FC<OrgAutocompleteProps> = ({
    value,
    onChange,
    disabled = false,
    className = '',
    placeholder,
}) => {
    const queryClient = useQueryClient()
    const {
        data: orgsData,
        isLoading: isLoadingOrgs,
        isError: isOrgsError,
        error: orgsError,
    } = useAdminOrgs()
    const {
        mutate: createOrg,
        isPending: isCreatingOrg,
        isError: isCreateError,
        error: createError,
        reset: resetCreateOrg,
    } = useCreateOrg()

    const [inputValue, setInputValue] = useState('')
    const [showDropdown, setShowDropdown] = useState(false)
    const [filteredOrgs, setFilteredOrgs] = useState<OrgDto[]>([])
    const [isTyping, setIsTyping] = useState(false)

    const orgMap = useMemo(() => {
        if (!orgsData?.orgs) return new Map<string, OrgDto>()
        return new Map(orgsData.orgs.map((org) => [org.id, org]))
    }, [orgsData])

    // Effect to set initial input value based on external `value` prop
    useEffect(() => {
        if (!isTyping) {
            if (value && orgMap.has(value)) {
                setInputValue(orgMap.get(value)?.name || '')
                resetCreateOrg() // Reset creation error if value changes externally
            } else {
                setInputValue('')
                if (value) {
                    onChange(null)
                }
            }
        }
    }, [value, orgMap, isTyping, onChange, resetCreateOrg])

    // Effect to filter orgs based on input
    useEffect(() => {
        if (!orgsData?.orgs) {
            setFilteredOrgs([])
            return
        }
        const lowerCaseInput = inputValue.toLowerCase().trim()
        if (lowerCaseInput === '') {
            setFilteredOrgs(orgsData.orgs)
        } else {
            const filtered = orgsData.orgs.filter((org) =>
                org.name.toLowerCase().includes(lowerCaseInput),
            )
            setFilteredOrgs(filtered)
        }
    }, [inputValue, orgsData])

    // Check if the current input value represents a potentially new organization
    const canCreateCurrentInput = useMemo(() => {
        const trimmedInput = inputValue.trim()
        if (!trimmedInput) return false // Cannot create empty name
        if (isLoadingOrgs || isOrgsError) return false // Don't allow creation if org list isn't ready
        return !orgsData?.orgs.some((org) => org.name.toLowerCase() === trimmedInput.toLowerCase())
    }, [inputValue, orgsData, isLoadingOrgs, isOrgsError])

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newInputValue = e.target.value
        setInputValue(newInputValue)
        setShowDropdown(true)
        setIsTyping(true)
        resetCreateOrg() // Reset creation error when user types
        // If input is cleared, notify parent
        if (newInputValue.trim() === '') {
            onChange(null)
        }
    }

    const handleSelectOrg = (org: OrgDto) => {
        setInputValue(org.name)
        onChange(org)
        setShowDropdown(false)
        setIsTyping(false)
        resetCreateOrg() // Reset creation error on selection
    }

    const handleAttemptCreate = () => {
        const newName = inputValue.trim()
        if (!canCreateCurrentInput || !newName || isCreatingOrg) {
            return // Don't create if not valid, empty, or already creating
        }

        const data: CreateOrgDto = { name: newName }
        createOrg(data, {
            onSuccess: (newlyCreatedOrg) => {
                // Invalidate query cache to refetch orgs
                queryClient.invalidateQueries({ queryKey: ['adminOrgs'] })
                // Optionally wait for refetch? Or assume immediate use is fine?
                setInputValue(newlyCreatedOrg.name) // Update input to newly created name
                onChange(newlyCreatedOrg) // Pass the new OrgDto to parent
                setShowDropdown(false)
                setIsTyping(false)
                // resetCreateOrg() // Success clears state implicitly usually
            },
            onError: (error) => {
                // Error state is handled by isCreateError/createError
                console.error('Error creating org:', error)
                // Potentially keep dropdown open to show error?
                setShowDropdown(true)
            },
        })
    }

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            e.preventDefault()
            const exactMatch = orgsData?.orgs.find(
                (org) => org.name.toLowerCase() === inputValue.trim().toLowerCase(),
            )

            if (exactMatch) {
                handleSelectOrg(exactMatch)
            } else if (canCreateCurrentInput) {
                handleAttemptCreate()
            } else {
                // Input doesn't match and cannot create (e.g., empty or matches exactly but case differs?)
                // Or maybe orgs haven't loaded yet. Close dropdown.
                setShowDropdown(false)
                setIsTyping(false)
            }
        }
        if (e.key === 'Escape') {
            setShowDropdown(false)
            setIsTyping(false)
            // Reset input to current value name if selected, otherwise clear
            if (value && orgMap.has(value)) {
                setInputValue(orgMap.get(value)?.name || '')
            } else {
                setInputValue('')
                onChange(null)
            }
            resetCreateOrg() // Reset error on escape
        }
    }

    const handleBlur = useCallback(() => {
        setIsTyping(false)
        // Delay slightly to allow click events on dropdown/create button
        setTimeout(() => {
            // Don't trigger create on blur if a dropdown item was just clicked
            // This check is tricky, often handled by preventing blur on mousedown in the dropdown

            const trimmedInput = inputValue.trim()
            const currentSelection = value ? orgMap.get(value) : null
            const exactMatch = orgsData?.orgs.find(
                (org) => org.name.toLowerCase() === trimmedInput.toLowerCase(),
            )

            if (exactMatch) {
                if (!currentSelection || currentSelection.id !== exactMatch.id) {
                    // Input matches an existing org different from current selection
                    handleSelectOrg(exactMatch) // Select it
                } else {
                    // Input matches the already selected org, do nothing, just close dropdown
                }
            } else if (canCreateCurrentInput) {
                // Input is new and valid for creation - maybe prompt user?
                // For now, let's NOT automatically create on blur, only on Enter or explicit click.
                // Reset input if it wasn't a valid selection?
                // If user typed something new and blurred, reset to last valid selection or clear.
                if (currentSelection) {
                    setInputValue(currentSelection.name)
                } else {
                    setInputValue('')
                    onChange(null)
                }
                resetCreateOrg()
            } else {
                // Input is empty, or invalid for creation (e.g. exact match), or orgs not loaded
                // Reset to last selection or clear
                if (currentSelection) {
                    setInputValue(currentSelection.name)
                } else {
                    setInputValue('')
                    onChange(null)
                }
                resetCreateOrg()
            }
            // Always close dropdown on blur after timeout
            setShowDropdown(false)
        }, 200) // Increased delay slightly
    }, [value, orgMap, inputValue, onChange, orgsData, canCreateCurrentInput, resetCreateOrg])

    const defaultPlaceholder = isLoadingOrgs
        ? 'Loading orgs...'
        : isOrgsError
        ? 'Error loading orgs'
        : '-- Select or type to create Organization --'

    // Combine disabled states
    const isDisabled = disabled || isLoadingOrgs || isCreatingOrg

    return (
        <div className={`relative ${className}`}>
            <input
                type="text"
                value={inputValue}
                onChange={handleInputChange}
                onFocus={() => {
                    setShowDropdown(true)
                    setIsTyping(true)
                    resetCreateOrg()
                }}
                onBlur={handleBlur}
                onKeyDown={handleKeyDown}
                placeholder={placeholder || defaultPlaceholder}
                className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 ${
                    isDisabled ? 'bg-gray-100' : ''
                } ${
                    isOrgsError || isCreateError ? 'border-red-300 text-red-600' : 'border-gray-300'
                }`}
                disabled={isDisabled}
                aria-autocomplete="list"
                aria-expanded={showDropdown}
            />
            {/* Loading/Error Messages */}
            {isOrgsError && !isLoadingOrgs && (
                <p className="text-xs text-red-500 mt-1">
                    Error loading org list:{' '}
                    {orgsError instanceof Error ? orgsError.message : 'Unknown error'}
                </p>
            )}
            {isCreateError && (
                <p className="text-xs text-red-500 mt-1">
                    Error creating org:{' '}
                    {createError instanceof Error ? createError.message : 'Unknown error'}
                </p>
            )}
            {isCreatingOrg && (
                <p className="text-xs text-blue-500 mt-1">Creating organization...</p>
            )}

            {/* Dropdown List */}
            {showDropdown && !isLoadingOrgs && !isOrgsError && (
                <ul className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
                    {filteredOrgs.map((org) => (
                        <li
                            key={org.id}
                            onMouseDown={(e) => {
                                e.preventDefault()
                                handleSelectOrg(org)
                            }}
                            className="px-3 py-2 cursor-pointer hover:bg-indigo-100"
                        >
                            {org.name}
                        </li>
                    ))}
                    {/* Show Create option only if input is valid and doesn't match existing */}
                    {canCreateCurrentInput && (
                        <li
                            onMouseDown={(e) => {
                                e.preventDefault()
                                handleAttemptCreate()
                            }}
                            className="px-3 py-2 cursor-pointer hover:bg-green-100 text-green-700"
                        >
                            Create "{inputValue.trim()}"
                        </li>
                    )}
                    {/* No results message */}
                    {filteredOrgs.length === 0 && !canCreateCurrentInput && (
                        <li className="px-3 py-2 text-sm text-gray-500">
                            {inputValue.trim()
                                ? `No matching organizations found.`
                                : `Type to search or create.`}
                        </li>
                    )}
                </ul>
            )}
        </div>
    )
}

export default OrgAutocomplete
